import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useCRM } from '../contexts/CRMContext';
import { format } from 'date-fns';

const { FiPlus, FiCheck, FiClock, FiAlertCircle, FiCheckSquare } = FiIcons;

const Tasks = () => {
  const { tasks, updateTask } = useCRM();
  const [filter, setFilter] = useState('all');

  const filteredTasks = tasks.filter(task => {
    switch (filter) {
      case 'pending': return !task.completed;
      case 'completed': return task.completed;
      case 'overdue': return !task.completed && new Date(task.due_date) < new Date();
      default: return true;
    }
  });

  const handleToggleComplete = async (taskId, completed) => {
    try {
      await updateTask(taskId, { completed: !completed });
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const isOverdue = (dueDate, completed) => {
    return !completed && new Date(dueDate) < new Date();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Tasks</h1>
          <p className="text-gray-600">Manage your tasks and activities</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="h-5 w-5" />
          <span>Add Task</span>
        </motion.button>
      </div>

      <div className="flex space-x-4 mb-6">
        {['all', 'pending', 'completed', 'overdue'].map((filterType) => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            className={`px-4 py-2 rounded-lg capitalize transition-colors ${
              filter === filterType
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {filterType}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="divide-y divide-gray-200">
          {filteredTasks.map((task) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`p-6 hover:bg-gray-50 ${task.completed ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start space-x-4">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleToggleComplete(task.id, task.completed)}
                  className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center ${
                    task.completed
                      ? 'bg-green-500 border-green-500'
                      : 'border-gray-300 hover:border-green-500'
                  }`}
                >
                  {task.completed && (
                    <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                  )}
                </motion.button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className={`text-sm font-medium ${
                        task.completed ? 'line-through text-gray-500' : 'text-gray-900'
                      }`}>
                        {task.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                      
                      <div className="flex items-center space-x-4 mt-3">
                        <div className="flex items-center text-sm text-gray-500">
                          <SafeIcon icon={FiClock} className="w-4 h-4 mr-1" />
                          {format(new Date(task.due_date), 'MMM dd, yyyy')}
                        </div>
                        
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>

                        {isOverdue(task.due_date, task.completed) && (
                          <div className="flex items-center text-sm text-red-600">
                            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1" />
                            Overdue
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiCheckSquare} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No tasks found</p>
            <p className="text-gray-400 text-sm">Create your first task to get organized</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tasks;